import React from 'react';
import { Bot } from 'lucide-react';
import { sendCloudIncidentChatMessage } from '@/api/websocket';
import { CloudIncidentSummary } from '@/api/client';
import { BaseChat } from '@/components/chat/BaseChat';
import { CloudFixesDisplay } from '@/components/chat/CloudFixesDisplay';
import { ChatMessage } from '@/components/chat/chat-utils';

interface CloudIncidentChatProps {
  incident: CloudIncidentSummary;
}

export function CloudIncidentChat({ incident }: CloudIncidentChatProps) {
  const getCloudProvider = (): 'aws' | 'oci' => {
    return (incident.cloud_provider as 'aws' | 'oci') || 'aws';
  };

  const provider = getCloudProvider();

  return (
    <BaseChat
      config={{
        sendMessage: (inputText: string) =>
          sendCloudIncidentChatMessage(inputText, incident.id, incident, {
            cloud_provider: provider,
            aws_account_id: incident.aws_account_id,
            aws_connection_id: incident.aws_connection_id,
            oci_tenancy_ocid: incident.oci_tenancy_ocid,
            oci_connection_id: incident.oci_connection_id,
          }),
        samplePrompts: [
          "What caused this incident?",
          provider === 'oci'
            ? "Show me OCI CLI commands to investigate further"
            : "Show me AWS CLI commands to investigate further",
          "How can I prevent this from happening again?",
          "Generate Terraform to fix this issue",
        ],
        headerIcon: (
          <div className="relative">
            <Bot className="relative h-6 w-6 text-foreground" />
          </div>
        ),
        headerTitle: "AI Incident Assistant",
        emptyHeading: "Ask me about this cloud incident",
        emptyDescription: `I can help investigate this ${provider === 'oci' ? 'OCI' : 'AWS'} incident, suggest fixes, and provide ${provider === 'oci' ? 'OCI CLI' : 'AWS CLI'} commands.`,
        inputPlaceholder: "Ask about this cloud incident...",
        colorTheme: 'primary',
        fixType: 'cloud',
        renderFixes: (message: ChatMessage) =>
          message.cloudFixes ? <CloudFixesDisplay cloudFixes={message.cloudFixes} /> : null,
        renderStreamingFixes: (fixes) =>
          <CloudFixesDisplay cloudFixes={fixes as any} />,
        fixesLoadingText: "Loading cloud fixes",
        contextType: 'cloud_incident',
        contextId: incident.id,
      }}
    />
  );
}
